'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { Check, FileSpreadsheet } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'
import { ExplainerFrame } from './explainer-frame'

// The Amazon Pack in one glance: a rights-cleared post goes through the
// compliance rules Amazon actually enforces, gets rendered to the A+
// module spec, and comes out as a manifest-backed upload. The rule list
// mirrors the deterministic engine (no handles or watermarks, no review
// quotes, no pricing or CTA, exact module dimensions, rights scope).

const RULES = [
  'No social handles or watermarks',
  'No review quotes or ratings',
  'No pricing, promo or CTA text',
  'Cropped to 1464 × 600, never upscaled',
  'Rights scope: amazon_organic',
]

export function AmazonPackExplainer({ className }: { className?: string }) {
  const base = 0.9
  const step = 0.28
  const moduleAt = base + RULES.length * step + 0.2
  return (
    <ExplainerFrame
      eyebrow="Amazon syndication"
      title="From UGC to an A+ module"
      caption="Every asset in a pack passes the same checks Amazon's reviewers apply, then lands in Seller Central with a manifest that names the ASIN and module for each file."
      className={className}
    >
      {({ play, reduce, d }) => (
        <LazyMotion features={domAnimation}>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)]">
            <div>
              <m.div
                className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-primary/10 to-primary/30"
                initial={reduce ? false : { opacity: 0, y: 10 }}
                animate={play ? { opacity: 1, y: 0 } : undefined}
                transition={{ duration: 0.4, ease: EASE, delay: d(0.2) }}
              >
                <m.span
                  className="absolute left-3 top-3 rounded-full bg-card/90 px-2 py-1 text-[10px] font-medium text-foreground backdrop-blur"
                  initial={reduce ? { opacity: 0 } : { opacity: 1 }}
                  animate={play ? { opacity: 0 } : undefined}
                  transition={{ duration: 0.3, delay: d(base + 0.1) }}
                >
                  @darcy.s
                </m.span>
                <div className="absolute bottom-[18%] left-[22%] h-[42%] w-[56%] rounded-[45%] bg-foreground/10" aria-hidden />
                {/* The crop frame that lands on the 1464×600 module aspect */}
                <m.div
                  className="absolute inset-x-2 top-[38%] aspect-[1464/600] rounded-md border-2 border-primary"
                  initial={reduce ? false : { opacity: 0, scale: 1.08 }}
                  animate={play ? { opacity: 1, scale: 1 } : undefined}
                  transition={{ ...SPRING, delay: d(base + 3 * step) }}
                  aria-hidden
                />
              </m.div>
              <p className="mt-2 text-[11px] text-muted-foreground">Rights-approved post, ASIN B0C4WATCH</p>
            </div>

            <div className="flex flex-col gap-3">
              <ul className="space-y-1.5">
                {RULES.map((r, i) => (
                  <m.li
                    key={r}
                    className="flex items-center gap-2 text-xs text-foreground"
                    initial={reduce ? false : { opacity: 0, x: -8 }}
                    animate={play ? { opacity: 1, x: 0 } : undefined}
                    transition={{ duration: 0.3, ease: EASE, delay: d(base + i * step) }}
                  >
                    <m.span
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white"
                      initial={reduce ? false : { scale: 0.4 }}
                      animate={play ? { scale: 1 } : undefined}
                      transition={{ ...SPRING, delay: d(base + i * step + 0.12) }}
                      aria-hidden
                    >
                      <Check className="h-2.5 w-2.5" />
                    </m.span>
                    {r}
                  </m.li>
                ))}
              </ul>

              <m.div
                className="rounded-2xl border bg-card p-3"
                initial={reduce ? false : { opacity: 0, y: 10 }}
                animate={play ? { opacity: 1, y: 0 } : undefined}
                transition={{ duration: 0.4, ease: EASE, delay: d(moduleAt) }}
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">A+ module · STANDARD_IMAGE_TEXT</p>
                <div className="mt-2 grid grid-cols-[1.4fr_1fr] gap-2">
                  <span className="aspect-[1464/600] rounded-md bg-gradient-to-br from-secondary via-primary/10 to-primary/30" aria-hidden />
                  <div className="space-y-1.5" aria-hidden>
                    <span className="block h-2 w-3/4 rounded bg-foreground/20" />
                    <span className="block h-2 w-full rounded bg-foreground/10" />
                    <span className="block h-2 w-5/6 rounded bg-foreground/10" />
                  </div>
                </div>
                <m.p
                  className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-1 font-mono text-[10px] text-foreground"
                  initial={reduce ? false : { opacity: 0 }}
                  animate={play ? { opacity: 1 } : undefined}
                  transition={{ duration: 0.3, delay: d(moduleAt + 0.35) }}
                >
                  <FileSpreadsheet className="h-3 w-3 text-primary" aria-hidden /> manifest.csv · B0C4WATCH/aplus/standard-image-text-1464x600-post812.jpg
                </m.p>
              </m.div>
            </div>
          </div>
        </LazyMotion>
      )}
    </ExplainerFrame>
  )
}
